<template>
  <SeranovaModal
    data-cy="refund-modal"
    size="large"
    body-padding="none"
    show-close-button
    :model-value="true"
    @update:model-value="(v: boolean) => !v && $emit('hide-modal')"
  >
    <template #header>
      <div
        class="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-gray-700"
        data-cy="refund-modal-title-row"
      >
        <strong class="font-semibold text-gray-900"
          >Refund · Invoice #{{ invoice.invoice_id }}</strong
        >
        <span class="text-gray-400">·</span>
        <span>{{ invoice.customer?.full_name }}</span>
      </div>
    </template>

    <div
      class="refundModalV2 flex flex-col divide-y divide-gray-200 [&>*]:px-5 [&>*]:py-3"
    >
      <div
        class="flex items-start gap-2 text-sm text-red-700 bg-red-50/40"
        role="alert"
        data-cy="refund-modal-final-banner"
      >
        <WarningIcon class="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Refunds are final.</strong> Once submitted, this transaction
          cannot be reversed from Sera.
        </div>
      </div>

      <section data-cy="refund-modal-source-section" aria-label="Refund method">
        <div class="block text-sm font-medium text-gray-900 mb-2">Method</div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 items-stretch">
          <RefundSourceTile
            v-for="tx in cardTransactions"
            :key="tx.id"
            :value="`card-${tx.id}`"
            :selected="selectedSourceKey === `card-${tx.id}`"
            kind="card"
            :refundable-cents="tx.refundable_amount_cents"
            :original-cents="tx.amount_cents"
            :already-refunded-cents="
              tx.amount_cents - tx.refundable_amount_cents
            "
            :card-brand="tx.card_brand"
            :card-last4="tx.card_last4"
            :payer-name="tx.payer_name"
            :charged-label="shortDate(tx.transaction_time)"
            :data-cy="`refund-source-tile-card-${tx.id}`"
            @select="selectSource(`card-${tx.id}`)"
          />
          <RefundSourceTile
            :value="'cash'"
            :selected="selectedSourceKey === 'cash'"
            kind="cash"
            :refundable-cents="invoiceRefundableCents"
            data-cy="refund-source-tile-cash"
            @select="selectSource('cash')"
          />
        </div>
      </section>

      <section data-cy="refund-modal-amount-section" aria-label="Refund amount">
        <div class="block text-sm font-medium text-gray-900 mb-2">Amount</div>
        <div
          class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_230px] gap-4 md:gap-6 items-start"
        >
          <div>
            <MoneyAmountField
              v-model="refundAmountCents"
              tone="refund"
              data-cy="refund-modal-amount-input"
            >
              <template #hint>
                <span data-cy="refund-modal-cap"
                  >Refundable using this method:
                  <strong
                    class="font-mono tabular-nums text-gray-900 font-medium"
                    >{{ dollars(currentRefundableCents) }}</strong
                  ></span
                >
                <button
                  v-if="refundAmountCents !== currentRefundableCents"
                  type="button"
                  class="text-xs font-semibold underline underline-offset-2 text-blue-700"
                  data-cy="refund-modal-fill-full"
                  @click="refundAmountCents = currentRefundableCents"
                >
                  Refund this amount
                </button>
              </template>
            </MoneyAmountField>

            <InlineNote
              v-if="sameDayPartialNoteVisible"
              tone="info"
              class="mt-3"
              data-cy="refund-modal-sameday-note"
            >
              Card <strong>partial</strong> refunds can only be processed after
              <strong>one business day</strong>. You can still issue the full
              refund today, or wait until tomorrow.
            </InlineNote>

            <InlineNote
              v-if="overCapWarning"
              tone="warn"
              class="mt-3"
              data-cy="refund-modal-over-cap"
            >
              Refund exceeds the refundable amount ({{
                dollars(currentRefundableCents)
              }}). Lower the amount or pick a different source.
            </InlineNote>
          </div>

          <aside
            class="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-xs space-y-1"
            data-cy="refund-modal-destination"
          >
            <div class="text-sm font-medium text-gray-900 mb-1">Refund to</div>
            <template v-if="selectedSourceIsCash">
              <div class="flex justify-between text-gray-700">
                <span>Method</span>
                <strong class="font-mono tabular-nums text-gray-900 font-medium"
                  >Cash or check</strong
                >
              </div>
              <!-- Spacer row keeps the destination card the same height as
                   the card variant (which has a Cardholder row) so the
                   sticky footer stays put when switching sources. -->
              <div
                class="flex justify-between text-gray-700"
                aria-hidden="true"
              >
                <span>&nbsp;</span>
                <span class="font-mono tabular-nums">&nbsp;</span>
              </div>
              <hr class="border-gray-200 my-1.5" />
              <div class="flex justify-between text-gray-700">
                <span>Settles</span>
                <strong class="font-mono tabular-nums text-gray-900 font-medium"
                  >Immediately</strong
                >
              </div>
              <div class="flex justify-between text-gray-700">
                <span>Receipt</span>
                <strong class="font-mono tabular-nums text-gray-900 font-medium"
                  >Manual</strong
                >
              </div>
            </template>
            <template v-else-if="selectedTransaction">
              <div class="flex justify-between text-gray-700">
                <span>Method</span>
                <strong class="font-mono tabular-nums text-gray-900 font-medium"
                  >{{ selectedTransaction.card_brand }} ••••
                  {{ selectedTransaction.card_last4 }}</strong
                >
              </div>
              <div class="flex justify-between text-gray-700">
                <span>Cardholder</span>
                <strong
                  class="font-mono tabular-nums text-gray-900 font-medium"
                  >{{ selectedTransaction.payer_name }}</strong
                >
              </div>
              <hr class="border-gray-200 my-1.5" />
              <div class="flex justify-between text-gray-700">
                <span>Settles in</span>
                <strong class="font-mono tabular-nums text-gray-900 font-medium"
                  >3–5 business days</strong
                >
              </div>
              <div class="flex justify-between text-gray-700">
                <span>Receipt</span>
                <strong class="font-mono tabular-nums text-gray-900 font-medium"
                  >Auto-emailed</strong
                >
              </div>
            </template>
          </aside>
        </div>
      </section>

      <section
        v-if="isMultiInvoiceRefund"
        data-cy="refund-modal-covered-invoices"
        aria-label="Invoices covered by this payment"
      >
        <div class="block text-sm font-medium text-gray-900 mb-2">
          This refund covers {{ coveredInvoiceAmounts.length }} invoices
        </div>
        <div class="rounded-lg border border-gray-200 divide-y divide-gray-100">
          <div
            v-for="row in coveredInvoiceAmounts"
            :key="row.invoice_id"
            class="flex items-center justify-between px-3 py-2 text-sm"
            :data-cy="`refund-modal-covered-invoice-${row.invoice_id}`"
          >
            <span class="font-medium text-blue-700"
              >Invoice #{{ row.invoice_id }}</span
            >
            <span class="font-mono tabular-nums text-gray-900">{{
              dollars(row.amount_cents)
            }}</span>
          </div>
        </div>
        <InlineNote
          tone="info"
          class="mt-3"
          data-cy="refund-modal-covered-note"
        >
          This payment was applied across
          {{ coveredInvoiceAmounts.length }} invoices. Refunding it reverses
          <strong>all of them in full</strong> ({{
            dollars(currentRefundableCents)
          }}) — which is why the amount is more than Invoice #{{
            invoice.invoice_id
          }}'s balance.
        </InlineNote>
      </section>

      <section data-cy="refund-modal-notes-section">
        <label
          for="refund-modal-notes"
          class="block text-sm font-medium text-gray-900 mb-2"
        >
          Notes
        </label>
        <textarea
          id="refund-modal-notes"
          v-model="notes"
          rows="2"
          placeholder="Reason for refund (kept on the invoice record)"
          data-cy="refund-modal-notes"
          class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 resize-y focus:outline-none focus:ring-1 focus:border-blue-500 focus:ring-blue-500"
        ></textarea>
      </section>

      <section
        v-if="partialRefundReconciliationEnabled"
        data-cy="refund-modal-apply-credit-section"
      >
        <div class="flex items-start gap-3">
          <Toggle
            :checked="applyAsCredit"
            data-cy="refund-modal-apply-credit-toggle"
            @update:checked="(v) => (applyAsCredit = v)"
          />
          <div class="text-sm text-gray-900 leading-snug">
            <div class="font-medium">Apply refund as credit to invoice</div>
            <div class="text-xs text-gray-700 mt-1 leading-snug">
              Reduces the invoice balance by the refund amount; otherwise, the
              balance is owed again for re-collection.
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="canCancelPaymentPlan"
        data-cy="refund-modal-cancel-plan-card"
        class="mb-2"
      >
        <div
          class="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 text-sm"
        >
          <div
            class="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2"
          >
            <WarningIcon class="h-3 w-3" />
            Also cancel the {{ programName }}?
          </div>
          <div class="flex items-start gap-3">
            <Toggle
              :checked="cancelPaymentPlan"
              data-cy="refund-modal-cancel-plan-checkbox"
              @update:checked="(v) => (cancelPaymentPlan = v)"
            />
            <div class="text-sm text-gray-900 leading-snug">
              <span>
                Cancel the remaining
                <strong
                  class="font-mono tabular-nums font-semibold"
                  data-cy="refund-modal-remaining-installments"
                  >{{ remainingInstallmentsCount }}
                  {{ intervalNamePlural.toLowerCase() }}</strong
                >
                (<strong
                  class="font-mono tabular-nums font-semibold"
                  data-cy="refund-modal-remaining-cents"
                  >{{ dollars(remainingInstallmentsCents) }}</strong
                >
                total).
              </span>
              <div class="text-xs text-gray-700 mt-1 leading-snug">
                This stops all future scheduled
                {{ intervalNamePlural.toLowerCase() }} for this invoice and
                cannot be undone. Existing receipts are unaffected.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <StickyConfirmFooter
        tone="refund"
        :amount-cents="refundAmountCents"
        :destination="confirmDestination"
        :saving="saving"
        :disabled="!canSubmit"
        @submit="handleSubmit"
        @cancel="$emit('hide-modal')"
      />
    </template>
  </SeranovaModal>
</template>

<script lang="ts">
import moment from "moment-timezone";
import { mapActions, mapGetters } from "vuex";
import { trackEvent } from "@/lib/pendo";

import InlineNote from "@/components/PaymentModals/InlineNote.vue";
import MoneyAmountField from "@/components/PaymentModals/MoneyAmountField.vue";
import RefundSourceTile from "@/components/PaymentModals/RefundSourceTile.vue";
import StickyConfirmFooter from "@/components/PaymentModals/StickyConfirmFooter.vue";
import SeranovaModal from "@/components/Seranova/Modal/Modal.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import WarningIcon from "@/images/Warning.svg?component";
import { dollars, shortDate } from "@/mixins";
import type { Invoice } from "@/typings/invoice";
import { type Transaction, TransactionType } from "@/typings/transaction";

export default {
  name: "RefundModalV2",
  components: {
    InlineNote,
    MoneyAmountField,
    RefundSourceTile,
    SeranovaModal,
    StickyConfirmFooter,
    Toggle,
    WarningIcon
  },
  props: {
    invoice: {
      type: Object as () => Invoice,
      required: true
    }
  },
  emits: ["hide-modal"],
  data() {
    return {
      saving: false,
      selectedSourceKey: "" as string,
      refundAmountCents: 0,
      notes: "",
      cancelPaymentPlan: false,
      applyAsCredit: false
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    programName(): string {
      return (
        (this as any).$store.state.tenant?.feature_flags
          ?.progress_billing_program_name ||
        this.invoice?.progress_billing?.program_name ||
        "Payment Schedule"
      );
    },
    intervalNamePlural(): string {
      return (
        (this as any).$store.state.tenant?.feature_flags
          ?.progress_billing_interval_name_plural ||
        this.invoice?.progress_billing?.interval_name_plural ||
        "Installments"
      );
    },
    cardTransactions(): Transaction[] {
      return (this.invoice.payments || []).filter(
        (t: Transaction) =>
          t.transaction_type === TransactionType.CARD &&
          t.links?.payment_refund &&
          (t.refundable_amount_cents ?? 0) > 0
      );
    },
    invoiceRefundableCents(): number {
      return this.invoice.financials?.refundable_amount_cents ?? 0;
    },
    selectedSourceIsCash(): boolean {
      return this.selectedSourceKey === "cash";
    },
    selectedTransaction(): Transaction | null {
      if (this.selectedSourceIsCash) return null;
      const id = Number(this.selectedSourceKey.replace(/^card-/, ""));
      if (!id) return null;
      return (
        (this.invoice.payments || []).find((t: Transaction) => t.id === id) ??
        null
      );
    },
    currentRefundableCents(): number {
      if (this.selectedSourceIsCash) return this.invoiceRefundableCents;
      return this.selectedTransaction?.refundable_amount_cents ?? 0;
    },
    isMultiInvoiceRefund(): boolean {
      return (
        !this.selectedSourceIsCash &&
        (this.selectedTransaction?.covered_invoice_count ?? 0) > 1
      );
    },
    coveredInvoiceAmounts(): { invoice_id: number; amount_cents: number }[] {
      return this.selectedTransaction?.covered_invoice_amounts ?? [];
    },
    isSameDayCardTransaction(): boolean {
      if (this.selectedSourceIsCash) return false;
      const t = this.selectedTransaction?.transaction_time;
      if (!t) return false;
      return moment().diff(moment(t), "hours") < 24;
    },
    sameDayPartialNoteVisible(): boolean {
      if (!this.isSameDayCardTransaction) return false;
      const v = this.refundAmountCents;
      return v > 0 && v < this.currentRefundableCents;
    },
    overCapWarning(): boolean {
      return this.refundAmountCents > this.currentRefundableCents;
    },
    confirmDestination(): string {
      if (this.selectedSourceIsCash) return "Cash/Check";
      const t = this.selectedTransaction;
      if (!t) return "";
      return `${t.card_brand} •••• ${t.card_last4}`;
    },
    canSubmit(): boolean {
      if (!this.selectedSourceKey) return false;
      if (this.refundAmountCents <= 0) return false;
      if (this.overCapWarning) return false;
      return true;
    },
    partialRefundReconciliationEnabled(): boolean {
      return this.featureEnabledForTenant("partial_refund_reconciliation");
    },
    canCancelPaymentPlan(): boolean {
      if (!this.featureEnabledForTenant("progress_billing")) return false;
      const plan = this.invoice?.progress_billing;
      if (!plan?.is_progress_billing) return false;
      if (plan.cancelled) return false;
      if (!plan.installments?.length) return false;
      return plan.installments.some(
        (i: { status: string }) =>
          i.status !== "paid" && i.status !== "cancelled"
      );
    },
    remainingInstallments(): Array<{ status: string; amount_cents?: number }> {
      const plan = this.invoice?.progress_billing;
      return (plan?.installments ?? []).filter(
        (i: { status: string }) =>
          i.status !== "paid" && i.status !== "cancelled"
      );
    },
    remainingInstallmentsCount(): number {
      return this.remainingInstallments.length;
    },
    remainingInstallmentsCents(): number {
      return this.remainingInstallments.reduce(
        (sum: number, i: any) => sum + (i.amount_cents ?? 0),
        0
      );
    }
  },
  watch: {
    invoice: {
      immediate: true,
      handler(inv: Invoice) {
        if (!inv) return;
        const card = (inv.payments || []).find(
          (t: Transaction) =>
            t.transaction_type === TransactionType.CARD &&
            t.links?.payment_refund &&
            (t.refundable_amount_cents ?? 0) > 0
        );
        if (card) {
          this.selectedSourceKey = `card-${card.id}`;
          this.refundAmountCents = card.refundable_amount_cents;
        } else {
          this.selectedSourceKey = "cash";
          this.refundAmountCents = inv.financials?.refundable_amount_cents ?? 0;
        }
      }
    }
  },
  methods: {
    ...mapActions(["createRefund"]),
    dollars,
    shortDate,
    selectSource(key: string) {
      this.selectedSourceKey = key;
      // Snap the amount to the new source's refundable cap.
      this.refundAmountCents = this.currentRefundableCents;
    },
    async handleSubmit() {
      if (this.saving || !this.canSubmit) return;
      this.saving = true;
      try {
        const url = this.selectedTransaction
          ? this.selectedTransaction.links?.payment_refund
          : this.invoice.links?.invoice_refund;
        if (!url) {
          window.notify?.error?.(
            "This invoice cannot be refunded — no refund URL is available."
          );
          this.saving = false;
          return;
        }
        const cancellingPlan = this.canCancelPaymentPlan
          ? this.cancelPaymentPlan
          : false;
        await this.createRefund({
          url,
          data: {
            amount_cents: this.refundAmountCents,
            notes: this.notes,
            cancel_payment_plan: cancellingPlan,
            apply_as_credit: this.applyAsCredit
          }
        });
        if (cancellingPlan) {
          trackEvent("spb_plan_cancelled_on_refund", {
            invoice_id: this.invoice.invoice_id,
            refund_amount_cents: this.refundAmountCents,
            remaining_installments_count: this.remainingInstallmentsCount,
            remaining_installments_cents: this.remainingInstallmentsCents
          });
        }
        trackEvent("partial_refund_completed", {
          invoice_id: this.invoice.invoice_id,
          refund_amount_cents: this.refundAmountCents,
          refund_source: this.selectedTransaction ? "transaction" : "invoice"
        });
        window.notify?.success?.("Refund complete.");
        this.$emit("hide-modal");
      } catch (err) {
        // createRefund surfaces its own toast on failure
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
